import React, { useCallback, useMemo, useState } from 'react';

import {
  Avatar,
  Badge,
  Box,
  Divider,
  Group,
  Modal,
  Paper,
  px,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangleFilled,
  IconChecks,
  IconChevronDown,
  IconClipboardText,
} from '@tabler/icons-react';
import KiloButton from 'components/KiloButton';
import Menu, { MenuItemProps } from 'components/Menu';
import ModalLateCancelNoShow from 'components/ModalLateCancelNoShow';
import ReservationAlertBadges from 'components/ReservationAlertBadges/ReservationAlertBadges';
import { useStaffTracking } from 'hooks/mixpanel/useStaffTracking';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AppointmentRetrieve,
  AthleteAppointmentWithAthlete,
  AthleteKiloClassWithAthlete,
  KiloClassRetrieve,
} from 'types';

import EventNotesContentModal from './EventNotesContentModal';
import ModalAthleteAppointmentDelete from '../../Scheduling/components/EventCalendar/components/EditAppointmentDrawer/components/ReservationsForm/components/ModalAthleteAppointmentDelete';
import ModalAthleteClassCreateBulk from '../../Scheduling/components/EventCalendar/components/EditClassDrawer/components/ReservationsForm/components/ModalAthleteClassCreateBulk';
import ModalAthleteClassDelete from '../../Scheduling/components/EventCalendar/components/EditClassDrawer/components/ReservationsForm/components/ModalAthleteClassDelete';
import { KioskRosterAthlete } from '../KioskDetail';

interface EventDetailCardProps {
  athlete: KioskRosterAthlete;
  onClick: (value: KioskRosterAthlete) => void;
  activeTab: string;
  isKioskMode?: boolean;
  isClassFull?: boolean;
  kiloClass?: KiloClassRetrieve;
  athleteKiloClass?: AthleteKiloClassWithAthlete;
  appointmentData?: AppointmentRetrieve;
  athleteAppointment?: AthleteAppointmentWithAthlete;
  athleteAppointmentsLength: number;
  setSearch: (value: string) => void;
  isLoading?: boolean;
}

const EventDetailCard = ({
  athlete,
  onClick,
  activeTab,
  isKioskMode,
  isClassFull,
  appointmentData,
  athleteAppointment,
  athleteKiloClass,
  kiloClass,
  athleteAppointmentsLength,
  setSearch,
  isLoading = false,
}: EventDetailCardProps): JSX.Element => {
  const mantineTheme = useMantineTheme();
  const navigate = useNavigate();
  const staffTracking = useStaffTracking();
  const isDesktop = useMediaQuery(`(min-width: ${mantineTheme.breakpoints.sm}`, true);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();

  const [deleteModalOpenedKiloClass, { open: openDeleteModalKiloClass, close: closeDeleteModalKiloClass }] =
    useDisclosure(false);
  const [
    deleteModalOpenedAppointment,
    { open: openDeleteModalAppointment, close: closeDeleteModalAppointment },
  ] = useDisclosure(false);
  const [isLateCancel, setIsLateCancel] = useState<boolean>(true);

  const [createBulkModalOpened, { open: openCreateBulkModal, close: closeCreateBulkModal }] =
    useDisclosure(false);
  const [
    lateCancelNoShowModalOpenedKiloClass,
    { open: openLateCancelNoShowModalKiloClass, close: closeLateCancelNoShowModalKiloClass },
  ] = useDisclosure(false);
  const [
    lateCancelNoShowModalOpenedAppointment,
    { open: openLateCancelNoShowModalAppointment, close: closeLateCancelNoShowModalAppointment },
  ] = useDisclosure(false);

  const eventType = useMemo(() => {
    return searchParams.get('eventType') as 'class' | 'appointment';
  }, [searchParams]);

  const isEventClass = useMemo(() => {
    return eventType === 'class';
  }, [eventType]);

  const isActiveTabEligible = useMemo(() => {
    return activeTab === 'eligible';
  }, [activeTab]);
  const {
    fullName,
    isCheckedIn,
    isReserved,
    profilePicture,
    hasPastDueBills,
    hasUnsignedDocuments,
    notes,
    isFirstClassVisit,
    athleteStatus,
  } = athlete;

  const isCheckInBlocked = useMemo(
    () => !!isKioskMode && isActiveTabEligible && !isReserved && !!isClassFull,
    [isActiveTabEligible, isClassFull, isKioskMode, isReserved],
  );

  const handleLateCancel = (): void => {
    setIsLateCancel(true);
    setSearch('');
    if (isEventClass) {
      openLateCancelNoShowModalKiloClass();
    } else {
      openLateCancelNoShowModalAppointment();
    }
  };

  const handleNoShow = (): void => {
    setSearch('');
    setIsLateCancel(false);
    if (isEventClass) {
      openLateCancelNoShowModalKiloClass();
    } else {
      openLateCancelNoShowModalAppointment();
    }
  };

  const handleBulkReservations = useCallback((): void => {
    openCreateBulkModal();
    setSearch('');
  }, [openCreateBulkModal, setSearch]);

  const handleRemoveReservations = useCallback((): void => {
    setSearch('');
    if (isEventClass) {
      openDeleteModalKiloClass();
    } else {
      openDeleteModalAppointment();
    }
  }, [isEventClass, openDeleteModalAppointment, openDeleteModalKiloClass, setSearch]);

  const bulkReservationsOption = useMemo(() => {
    return isEventClass ? [{ title: 'Bulk Reservation', onClick: handleBulkReservations }] : [];
  }, [isEventClass, handleBulkReservations]);

  const menuItems: MenuItemProps[] = useMemo(() => {
    let items: MenuItemProps[] = [
      ...bulkReservationsOption,
      {
        title: 'Remove Reservation',
        onClick: handleRemoveReservations,
      },
    ];

    if (athleteKiloClass?.isCredited || athleteAppointment?.isCredited) {
      items.unshift(
        ...[
          {
            title: 'Mark Late Cancel',
            onClick: () => handleLateCancel(),
            disabled: isCheckedIn,
          },
          {
            title: 'Mark No Show',
            onClick: () => handleNoShow(),
            disabled: isCheckedIn,
          },
        ],
      );
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkReservationsOption, isCheckedIn, athleteKiloClass?.isCredited, athleteAppointment?.isCredited]);

  const redirectToAthlete = (athleteId: string): void => {
    if (isKioskMode) return;
    navigate(`/users/athletes/${athleteId}/general-info`);
  };

  /// UI Elements

  const RemoveCheckInButton = (): JSX.Element => {
    if (isKioskMode) {
      return (
        <KiloButton
          variant='light'
          disabled={true}
          bg='transparent'
          color='#3D873A !Important'
          rootVariantType='kioskAthleteModeDisabled'
        >
          <Group mr={5}>
            <IconChecks size={20} />
          </Group>
          <Text>Checked In</Text>
        </KiloButton>
      );
    }

    return (
      <KiloButton variant='outline' onClick={() => onClick(athlete)} loading={isLoading}>
        <Text>Remove Check In</Text>
      </KiloButton>
    );
  };

  const DesktopView = (): JSX.Element => {
    return (
      <Paper
        radius={0}
        sx={() => ({
          borderTop: ' 1px solid #CED4DA',
          backgroundColor: isCheckedIn ? '#3CB5531A' : 'transparent',
        })}
      >
        <Group p={16} position='apart'>
          <Group>
            <Avatar size={54} radius={'100%'} src={profilePicture} />
            <Stack spacing={4}>
              <Text
                onClick={() => redirectToAthlete(athlete.athleteId)}
                weight={600}
                size={19}
                style={{ cursor: 'pointer' }}
              >
                {fullName}
              </Text>

              {!isKioskMode && (
                <ReservationAlertBadges
                  athleteStatus={athleteStatus}
                  isFirstClassVisit={isFirstClassVisit}
                  onBadgeViewed={badge =>
                    staffTracking.trackReservationAlertBadgeViewed({
                      badge,
                      surface: 'kiosk',
                      athlete_id: athlete.athleteId,
                    })
                  }
                />
              )}

              {hasPastDueBills && !isKioskMode && (
                <Badge
                  style={{ width: 'fit-content', fontWeight: 400, textTransform: 'none' }}
                  color='yellow'
                  size='xl'
                  radius={'sm'}
                  h={45}
                  p={9}
                  leftSection={
                    <Stack>
                      <IconAlertTriangleFilled />
                    </Stack>
                  }
                >
                  This account has a past-due bill.
                </Badge>
              )}
              {hasUnsignedDocuments && !isKioskMode && (
                <Badge
                  style={{ width: 'fit-content', fontWeight: 400, textTransform: 'none' }}
                  color='yellow'
                  size='xl'
                  radius={'sm'}
                  h={45}
                  p={9}
                  leftSection={
                    <Stack>
                      <IconAlertTriangleFilled />
                    </Stack>
                  }
                >
                  There is an unsigned document on this account.
                </Badge>
              )}
            </Stack>
          </Group>
          <Group align='center' spacing={8} noWrap>
            {isActiveTabEligible && (
              <Text color='#878E95' weight={400} size={12}>
                {isReserved ? 'Reserved' : 'Not Reserved'}
              </Text>
            )}
            <Group position='apart' align='center' noWrap>
              {notes?.length && !isKioskMode && (
                <>
                  <IconClipboardText
                    size={px(mantineTheme.fontSizes.sm) * 1.5}
                    strokeWidth={2}
                    color={mantineTheme.colors.kiloBlue[6]}
                    cursor={'pointer'}
                    onClick={() => setIsModalOpen(true)}
                  />
                  <Divider orientation='vertical' />
                </>
              )}
              {isCheckedIn ? (
                <RemoveCheckInButton />
              ) : (
                <>
                  {!isKioskMode && !isActiveTabEligible && (
                    <Menu
                      items={menuItems}
                      target={
                        <Box
                          w={40}
                          h={35}
                          sx={theme => ({
                            backgroundColor: theme.colors.kiloBlue[6],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: theme.radius.md,
                            cursor: 'pointer',
                          })}
                        >
                          <IconChevronDown color='white' />
                        </Box>
                      }
                      position='bottom-start'
                    />
                  )}
                  <Tooltip
                    label='You are not reserved for this class, ask your coach to check you in.'
                    disabled={!isCheckInBlocked}
                    multiline
                    width={220}
                  >
                    <Box style={{ display: 'inline-block' }}>
                      <KiloButton
                        onClick={() => onClick(athlete)}
                        loading={isLoading}
                        disabled={isCheckInBlocked}
                      >
                        <Text>Check In</Text>
                      </KiloButton>
                    </Box>
                  </Tooltip>
                </>
              )}
            </Group>
          </Group>
        </Group>
        {!isKioskMode && (
          <Modal
            opened={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={
              <Text px='xl' weight={700} size={'md'}>
                Pinned Notes
              </Text>
            }
            centered
            padding='xl'
            size={'xl'}
          >
            <Text weight={700} size={'md'} px={px(mantineTheme.spacing.xl) * 2} pb={'xl'}>
              {athlete.fullName}
            </Text>
            <Stack px={px(mantineTheme.spacing.xl) * 2}>
              {notes?.map(note => (
                <EventNotesContentModal note={note} key={note.noteId} />
              ))}
            </Stack>
          </Modal>
        )}
      </Paper>
    );
  };

  const MobileView = (): JSX.Element => {
    return (
      <Paper
        radius={0}
        p={16}
        sx={() => ({
          borderTop: ' 1px solid #CED4DA',
          backgroundColor: isCheckedIn ? '#3CB5531A' : 'transparent',
        })}
      >
        <Stack>
          <Group position='apart'>
            <Group style={{ flex: 1, overflow: 'hidden' }} noWrap>
              <Avatar size={54} radius={'100%'} src={profilePicture} />
              {/* Badges sit under the name here, not beside it. The name truncates at this width,
                  and inline badges left it as a single initial on a two-badge row. */}
              <Stack spacing={6} style={{ flex: 1, overflow: 'hidden' }}>
                <Text
                  onClick={() => redirectToAthlete(athlete.athleteId)}
                  weight={600}
                  size={19}
                  style={{ cursor: 'pointer' }}
                  truncate
                >
                  {fullName}
                </Text>
                {!isKioskMode && (
                  <ReservationAlertBadges
                    athleteStatus={athleteStatus}
                    isFirstClassVisit={isFirstClassVisit}
                    onBadgeViewed={badge =>
                      staffTracking.trackReservationAlertBadgeViewed({
                        badge,
                        surface: 'kiosk',
                        athlete_id: athlete.athleteId,
                      })
                    }
                  />
                )}
              </Stack>
            </Group>
            {isCheckedIn ? (
              <RemoveCheckInButton />
            ) : (
              <>
                {!isKioskMode && !isActiveTabEligible && (
                  <Menu
                    items={menuItems}
                    target={
                      <Box
                        w={40}
                        h={35}
                        sx={theme => ({
                          backgroundColor: theme.colors.kiloBlue[6],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: theme.radius.md,
                          cursor: 'pointer',
                        })}
                      >
                        <IconChevronDown color='white' />
                      </Box>
                    }
                    position='bottom-start'
                  />
                )}
                <Tooltip
                  label='You are not reserved for this class, ask your coach to check you in.'
                  disabled={!isCheckInBlocked}
                  multiline
                  width={220}
                >
                  <Box style={{ display: 'inline-block' }}>
                    <KiloButton onClick={() => onClick(athlete)} disabled={isCheckInBlocked}>
                      <Text>Check In</Text>
                    </KiloButton>
                  </Box>
                </Tooltip>
              </>
            )}
          </Group>
          <Stack
            spacing={4}
            pb={'0.75rem'}
            display={(!hasPastDueBills && !hasUnsignedDocuments) || !!isKioskMode ? 'none' : 'flex'}
          >
            {hasPastDueBills && !isKioskMode && (
              <Badge
                styles={{
                  root: {
                    width: '100%',
                    fontWeight: 400,
                    textTransform: 'none',
                    justifyContent: 'start',
                    whiteSpace: 'normal',
                  },
                  inner: {
                    whiteSpace: 'normal',
                  },
                }}
                color='yellow'
                size='xl'
                radius={'sm'}
                p={9}
                mih={45}
                h={'fit-content'}
                leftSection={
                  <Stack>
                    <IconAlertTriangleFilled />
                  </Stack>
                }
              >
                This account has a past-due bill.
              </Badge>
            )}
            {hasUnsignedDocuments && !isKioskMode && (
              <Badge
                styles={{
                  root: {
                    width: '100%',
                    fontWeight: 400,
                    textTransform: 'none',
                    justifyContent: 'start',
                    whiteSpace: 'normal',
                  },
                  inner: {
                    whiteSpace: 'normal',
                  },
                }}
                color='yellow'
                size='xl'
                radius={'sm'}
                p={9}
                mih={45}
                h={'fit-content'}
                leftSection={
                  <Stack>
                    <IconAlertTriangleFilled />
                  </Stack>
                }
              >
                There is an unsigned document on this account.
              </Badge>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  };

  return (
    <>
      {isDesktop ? <DesktopView /> : <MobileView />}

      {kiloClass && athleteKiloClass && (
        <>
          <ModalAthleteClassDelete
            opened={deleteModalOpenedKiloClass}
            handleClose={closeDeleteModalKiloClass}
            athleteKiloClassId={athleteKiloClass.athleteKiloClassId}
          />
          <ModalAthleteClassCreateBulk
            opened={createBulkModalOpened}
            handleClose={closeCreateBulkModal}
            athleteKiloClass={athleteKiloClass}
            kiloClass={kiloClass}
          />
          <ModalLateCancelNoShow
            opened={lateCancelNoShowModalOpenedKiloClass}
            handleClose={closeLateCancelNoShowModalKiloClass}
            isLateCancel={isLateCancel}
            eventType='class'
            athleteEventId={athleteKiloClass.athleteKiloClassId}
            athleteId={athleteKiloClass.athlete.athleteId}
            athleteName={`${athleteKiloClass.athlete.firstName} ${athleteKiloClass.athlete.lastName}`}
            eventId={athleteKiloClass.kiloClass}
            eventName={kiloClass.kiloClassType.name}
            gymId={kiloClass.gym}
            cancellationSettings={kiloClass.reservationAndCancellationSettings}
            triggeredBy='kiosk'
          />
        </>
      )}
      {athleteAppointmentsLength > 0 && athleteAppointment && (
        <>
          <ModalAthleteAppointmentDelete
            opened={deleteModalOpenedAppointment}
            handleClose={closeDeleteModalAppointment}
            athleteAppointmentId={athleteAppointment.athleteAppointmentId}
            handleCloseDrawer={() => {}}
            athleteAppointmentsLength={athleteAppointmentsLength}
          />
          <ModalLateCancelNoShow
            opened={lateCancelNoShowModalOpenedAppointment}
            isLateCancel={isLateCancel}
            handleClose={closeLateCancelNoShowModalAppointment}
            eventType='appointment'
            athleteEventId={athleteAppointment.athleteAppointmentId}
            athleteId={athleteAppointment.athlete.athleteId}
            athleteName={`${athleteAppointment.athlete.firstName} ${athleteAppointment.athlete.lastName}`}
            eventId={athleteAppointment.appointment}
            eventName={appointmentData?.appointmentType?.name ?? ''}
            gymId={appointmentData?.gym}
            cancellationSettings={appointmentData?.cancellationSettings}
            triggeredBy='kiosk'
          />
        </>
      )}
    </>
  );
};

export default EventDetailCard;
